import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Music, Layers, Headphones, Clock, Play, Pause, SkipBack, SkipForward,
  MoreHorizontal, ListMusic, ChevronDown, Sparkles, Monitor, Download, X,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { usePlayer } from '@/context/PlayerContext';
import { API, useAppContext } from '@/App';
import { useDashboardWidgets, SIZE_CLASSES } from '@/utils/dashboardWidgets';
import WaveformBars from '@/components/WaveformBars';
import SafeImg from '@/components/SafeImg';
import CamelotWheel from '@/components/CamelotWheel';
import TrackCoverButton from '@/components/TrackCoverButton';

const STARTER_TRACKS = [
  { i: 1, t: 'Eclipse', a: 'ANNA', bpm: 126, key: '8A', e: 7.2 },
  { i: 2, t: 'The Cycle', a: 'Kevin de Vries', bpm: 127, key: '9A', e: 7.6 },
  { i: 3, t: 'Tension', a: 'I Hate Models', bpm: 128, key: '9B', e: 8.1, keyAccent: true },
  { i: 4, t: 'Hidden Places', a: 'Adam Beyer', bpm: 129, key: '10A', e: 8.4, keyAccent: true },
  { i: 5, t: 'Rave Signal', a: 'Dax J', bpm: 130, key: '10B', e: 8.8, keyAccent: true },
  { i: 6, t: 'Into the Rhythm', a: 'Enrico Sangiuliano', bpm: 131, key: '11A', e: 9.1, amber: true },
  { i: 7, t: 'The Awakening', a: 'Charlotte de Witte', bpm: 131, key: '11B', e: 9.3, amber: true },
];

function formatHours(secs) {
  if (!secs || secs <= 0) return '0h';
  const h = Math.round(secs / 3600);
  return `${h}h`;
}

export default function DashboardPage() {
  const { user } = useAuth() || {};
  const ctx = useAppContext() || {};
  const trackCount = ctx.trackCount;
  const playlistCount = ctx.playlistCount;
  const navigate = useNavigate();
  const { playingTrack, isPlaying, playTrack } = usePlayer() || {};

  const [libCtx, setLibCtx] = useState(null);
  const [crateCount, setCrateCount] = useState(0);
  const [totalPlaytimeSec, setTotalPlaytimeSec] = useState(0);
  const [latestSet, setLatestSet] = useState(null); // {playlist, tracks} of newest AI-generated set

  const [activeRow, setActiveRow] = useState(1);

  // Wire the "play" buttons on the Latest AI Set rows + miniplayer to the
  // global PlayerContext. The set's full track list becomes the queue so
  // skip-next / skip-prev work afterwards.
  const playSetTrack = useCallback((row) => {
    if (!row?.id || !latestSet?.tracks?.length) return;
    const track = latestSet.tracks.find((t) => t.id === row.id);
    if (!track) return;
    // If the user clicks the currently-playing row, treat as toggle.
    if (playingTrack?.id === track.id) {
      // PlayerContext's playTrack already toggles play state when the same
      // track is re-fired (it sets togglePlay timestamp); reuse that here.
      playTrack(track, latestSet.tracks);
      return;
    }
    playTrack(track, latestSet.tracks);
    setActiveRow(row.i);
  }, [latestSet, playingTrack, playTrack]);

  useEffect(() => {
    axios.get(`${API}/dj/library-context`).then((r) => {
      setLibCtx(r.data);
      if (typeof r.data?.total_playtime_seconds === 'number') {
        setTotalPlaytimeSec(r.data.total_playtime_seconds);
      }
    }).catch(() => {});
    axios.get(`${API}/crates`).then((r) => {
      const list = Array.isArray(r.data) ? r.data : (r.data?.crates || []);
      setCrateCount(list.length);
    }).catch(() => {});
    // Fetch the most recent AI-generated set + its tracks
    axios.get(`${API}/playlists`).then(async (r) => {
      const pls = Array.isArray(r.data) ? r.data : (r.data?.playlists || []);
      // Newest is_suggested first (the API already returns newest-first)
      const ai = pls.find((p) => p.is_suggested);
      if (!ai) return;
      try {
        const { data } = await axios.get(`${API}/playlists/${ai.id}`);
        setLatestSet({ playlist: data.playlist, tracks: data.tracks || [] });
      } catch { /* keep STARTER fallback */ }
    }).catch(() => {});
  }, []);

  const totalTracks = libCtx?.tracks_count ?? trackCount ?? 0;
  const totalCrates = libCtx?.crate_count ?? crateCount ?? 0;
  const totalSets = libCtx?.playlist_count ?? playlistCount ?? 0;
  const keyMatchScore = 92;

  // Build the rows for the "AI Set Builder" preview table — prefer the real
  // latest AI-generated set; fall back to STARTER_TRACKS as a placeholder
  // when the user hasn't built one yet.
  const previewRows = (latestSet?.tracks?.length ? latestSet.tracks : null);
  const displayRows = previewRows
    ? (() => {
        // Belt-and-braces: dedupe at display time by (id) AND by (title|artist)
        // so any legacy set built before the backend dedupe lands cleanly too.
        const seenIds = new Set();
        const seenSigs = new Set();
        const cleaned = [];
        for (const t of previewRows) {
          if (!t?.id || seenIds.has(t.id)) continue;
          const sig = `${(t.title || '').trim().toLowerCase()}|${(t.artist || '').trim().toLowerCase()}`;
          if (sig.length > 1 && seenSigs.has(sig)) continue;
          seenIds.add(t.id);
          seenSigs.add(sig);
          cleaned.push(t);
        }
        return cleaned.slice(0, 7).map((t, i) => {
          const bpm = (t.analysis?.bpm) ? Math.round(t.analysis.bpm) : 0;
          const energyRaw = (t.analysis?.energy ?? 0);
          const e = energyRaw > 1.5 ? energyRaw / 10 : energyRaw * 10;
          return {
            id: t.id,
            i: i + 1,
            t: t.title || 'Untitled',
            a: t.artist || 'Unknown artist',
            bpm: bpm || '—',
            key: t.analysis?.camelot || '—',
            e: Math.round(e * 10) / 10,
            track: t, // keep the full track so the mini-player can render its real waveform
          };
        });
      })()
    : STARTER_TRACKS;

  const setName = latestSet?.playlist?.name || 'Techno Peak Time Set';
  const setDescription = latestSet?.playlist?.description || '';
  const setTrackCount = latestSet?.tracks?.length;

  // Pull metadata chips from the playlist description (Phase 2 stores curve / vibe / BPM there)
  const metaChips = useMemo(() => {
    if (!latestSet) return ['Techno', 'Peak Time', '128 BPM', '60 Tracks', '1h 32m'];
    const chips = [];
    const desc = setDescription;
    const bpmMatch = desc.match(/BPM\s*([\d–\-]+)/i);
    if (bpmMatch) chips.push(bpmMatch[0]);
    const curveMatch = desc.match(/Curve:\s*([a-z_]+)/i);
    if (curveMatch) chips.push(curveMatch[1].replace(/_/g, ' '));
    const vibeMatch = desc.match(/Vibe:\s*([^.]+)/i);
    if (vibeMatch) chips.push(vibeMatch[1].trim());
    if (setTrackCount) chips.push(`${setTrackCount} tracks`);
    if (latestSet.playlist?.created_at) {
      const ts = new Date(latestSet.playlist.created_at);
      if (!isNaN(ts.getTime())) {
        chips.push(ts.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }));
      }
    }
    return chips.slice(0, 5);
  }, [latestSet, setDescription, setTrackCount]);

  const { visibleWidgets, isEnabled, allWidgets } = useDashboardWidgets();

  /* Per-widget renderer. Returns the inner JSX (no outer sizing wrapper —
     that lives in the parent flex container). */
  const renderWidget = (id) => {
    switch (id) {
      case 'kpi_tracks':
        return <KpiCard icon={Music} label="Total Tracks" value={totalTracks.toLocaleString()} sub={`${totalTracks ? 'Live from your library' : 'Import to begin'}`} desc="Every track you've uploaded and analyzed." />;
      case 'kpi_crates':
        return <KpiCard icon={Layers} label="Total Crates" value={totalCrates} sub="Organized your way" desc="Crates are themed folders — group by vibe, client, or event." />;
      case 'kpi_sets':
        return <KpiCard icon={Headphones} label="Total Sets" value={totalSets} sub="Ready to play" desc="AI-generated and manually built sets combined." />;
      case 'kpi_playtime':
        return <KpiCard icon={Clock} label="Total Playtime" value={formatHours(totalPlaytimeSec)} sub="Across all sets" desc="Combined running time if you played every set back to back." />;
      case 'kpi_keymatch':
        return <ScoreKpi label="Key Match Score" value={keyMatchScore} sub="Excellent harmony" />;
      case 'latest_ai_set':
        return <LatestAiSetWidget
          latestSet={latestSet}
          setName={setName}
          metaChips={metaChips}
          displayRows={displayRows}
          activeRow={activeRow}
          setActiveRow={setActiveRow}
          playSetTrack={playSetTrack}
          playingTrack={playingTrack}
          isPlaying={isPlaying}
          navigate={navigate}
        />;
      case 'energy_flow':
        return <EnergyFlowCard tracks={latestSet?.tracks} />;
      case 'harmonic_map':
        return <HarmonicMapCard tracks={latestSet?.tracks} />;
      case 'set_insights':
        return <SetInsightsCard onView={() => navigate('/sets')} tracks={latestSet?.tracks} />;
      case 'transition_card':
        return <TransitionCard playingTrack={playingTrack} setTracks={latestSet?.tracks} navigate={navigate} />;
      default:
        return null;
    }
  };

  // Empty state when the user has hidden every widget
  if (visibleWidgets.length === 0) {
    return (
      <div className="px-4 sm:px-6 pb-6 flex items-center justify-center min-h-[60vh]" data-testid="dashboard-page">
        <div className="glass-pane p-10 text-center max-w-md">
          <p className="font-mono text-[10px] tracking-[0.32em] text-[#2563FF] mb-3">DASHBOARD</p>
          <h3 className="font-heading text-xl font-bold text-[#0F172A] mb-2">All widgets are hidden</h3>
          <p className="text-sm text-[#64748B] mb-5">
            Re-enable widgets from <Link to="/profile" className="text-[#2563FF] hover:underline font-semibold">Settings → Dashboard widgets</Link>.
          </p>
          <p className="text-xs text-[#94A3B8] font-mono">{allWidgets.length} widgets available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 pb-6" data-testid="dashboard-page">
      <DesktopAnalyzerBanner />
      <div className="flex flex-col gap-5" data-testid="widget-grid">
        {visibleWidgets.map((w) => (
          <div
            key={w.id}
            className="w-full min-w-0"
            data-testid={`widget-${w.id}`}
          >
            {renderWidget(w.id)}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ───────────────────── widget sub-components ───────────────────── */

/* Slim, dismissible banner promoting the desktop analyzer. Persists
   dismissal in localStorage so it doesn't nag repeat visitors. Sits at
   the very top of the dashboard, above the widget grid. */
function DesktopAnalyzerBanner() {
  const STORAGE_KEY = 'cratelab.dashboard.desktop-promo.dismissed';
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
  });

  if (dismissed) return null;

  const handleDismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch { /* private mode */ }
    setDismissed(true);
  };

  return (
    <div
      className="relative mb-5 overflow-hidden rounded-2xl border border-[#DBEAFE] bg-gradient-to-r from-[#EFF6FF] via-white to-[#F5F3FF] shadow-sm"
      data-testid="dashboard-desktop-promo"
    >
      {/* Ambient glow */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute -top-16 -left-8 w-56 h-56 bg-[#2563FF]/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-16 -right-8 w-56 h-56 bg-[#8B5CF6]/10 rounded-full blur-3xl" />
      </div>

      {/* Dismiss */}
      <button
        onClick={handleDismiss}
        className="absolute top-3 right-3 z-10 w-7 h-7 rounded-full flex items-center justify-center text-[#94A3B8] hover:text-[#0F172A] hover:bg-white/60 transition-colors"
        aria-label="Dismiss desktop analyzer promo"
        data-testid="dashboard-desktop-promo-dismiss"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="relative flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6 p-4 md:p-5 pr-10">
        {/* Icon tile */}
        <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-white border border-[#DBEAFE] flex items-center justify-center shadow-sm">
          <Monitor className="w-5 h-5 text-[#2563FF]" />
        </div>

        {/* Copy */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-[10px] uppercase tracking-wider text-[#2563FF] font-semibold">
              Desktop analyzer
            </span>
            <span className="px-1.5 py-0.5 rounded-md bg-[#2563FF] text-white font-mono text-[9px] uppercase tracking-wider">
              New
            </span>
          </div>
          <h3 className="font-heading text-base sm:text-lg font-bold text-[#0F172A] tracking-tight">
            Analyze your whole library locally
          </h3>
          <p className="font-body text-sm text-[#64748B] mt-1 leading-snug">
            Native Mac &amp; Windows app · Instant BPM, Camelot key &amp; energy detection · Two-way sync with your cloud library.
          </p>
        </div>

        {/* Mini Camelot chip */}
        <div className="hidden md:flex flex-shrink-0 flex-col items-center px-3 py-2 bg-white border border-[#E2E8F0] rounded-xl shadow-sm">
          <span className="font-mono text-[9px] uppercase tracking-wider text-[#94A3B8]">Key</span>
          <span className="font-heading text-lg font-bold text-[#2563FF] leading-none mt-0.5">6A</span>
          <span className="font-mono text-[9px] text-[#64748B] mt-1">128 BPM</span>
        </div>

        {/* CTA */}
        <div className="flex-shrink-0">
          <Link
            to="/profile"
            data-testid="dashboard-desktop-promo-cta"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#2563FF] hover:bg-[#1D4ED8] text-white rounded-lg font-heading text-sm uppercase tracking-wider transition-colors shadow-sm"
          >
            <Download className="w-4 h-4" />
            Download
          </Link>
        </div>
      </div>
    </div>
  );
}


function LatestAiSetWidget({
  latestSet, setName, metaChips, displayRows, activeRow, setActiveRow,
  playSetTrack, playingTrack, isPlaying, navigate,
}) {
  return (
    <section className="bg-white rounded-2xl border border-[#E2E8F0] shadow-sm p-6 h-full" data-testid="latest-ai-set">
        <div className="flex items-center justify-between mb-2">
          <p className="font-mono text-[10px] tracking-[0.18em] text-[#2563FF]">
            {latestSet ? 'LATEST AI-GENERATED SET' : 'AI SET BUILDER'}
          </p>
          <div className="flex items-center gap-3">
            {latestSet ? (
              <Link to="/sets" className="text-xs font-mono tracking-wider text-[#2563FF] hover:underline flex items-center gap-1" data-testid="latest-ai-set-open">
                <Sparkles className="w-3 h-3" /> ALL SETS
              </Link>
            ) : (
              <Link to="/sets" className="text-xs font-mono tracking-wider text-[#2563FF] hover:underline">BUILD ONE →</Link>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-4 min-w-0">
            <CoverStack
              tracks={displayRows.map((r) => r.track).filter(Boolean)}
              playlistId={latestSet?.playlist?.id}
            />
            <h2 className="font-heading text-2xl font-bold text-[#0F172A] tracking-tight truncate" data-testid="latest-ai-set-name">
              {setName}
            </h2>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mb-5">
          {metaChips.map((t, i) => (
            <span key={`${t}-${i}`} className={`px-3 py-1 rounded-full text-xs font-medium ${
              i < 2 ? 'gradient-primary text-white' : 'bg-[#EFF6FF] text-[#2563FF] border border-[#DBEAFE]'
            }`}>{t}</span>
          ))}
        </div>

        {/* Track table */}
        <div className="rounded-xl border border-[#E2E8F0] overflow-x-auto bg-white/50">
          <div className="md:min-w-[640px]">
          <div className="grid grid-cols-12 gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 border-b border-[#E2E8F0] font-mono text-[10px] uppercase tracking-wider text-[#94A3B8]">
            <div className="col-span-1">#</div>
            <div className="col-span-7 md:col-span-4">Track</div>
            <div className="hidden md:block md:col-span-3">Artist</div>
            <div className="col-span-2 md:col-span-1 text-center">BPM</div>
            <div className="col-span-2 md:col-span-1 text-center">Key</div>
            <div className="hidden md:block md:col-span-2 text-center">Energy</div>
          </div>
          {displayRows.map((row) => {
            const isActive = activeRow === row.i;
            const isThisPlaying = playingTrack?.id && playingTrack.id === row.id && isPlaying;
            return (
              <div key={row.id || row.i} className={`grid grid-cols-12 gap-2 sm:gap-3 items-center px-3 sm:px-4 py-3 border-b border-[#E2E8F0] last:border-0 transition-colors ${isActive ? 'bg-[#EFF6FF]' : 'hover:bg-[#F8FAFC]'}`}>
                <div className="col-span-1 flex items-center justify-center">
                  <TrackCoverButton
                    track={row.track}
                    isCurrentlyPlaying={!!isThisPlaying}
                    isPlaying={!!isPlaying}
                    onPlayPause={() => row.id ? playSetTrack(row) : setActiveRow(row.i)}
                    wrapperClassName="w-11 h-11"
                  />
                </div>
                <button
                  onClick={() => row.id ? navigate(`/track/${row.id}`) : setActiveRow(row.i)}
                  className="col-span-7 md:col-span-4 flex items-center gap-2 sm:gap-3 text-left min-w-0"
                  data-testid={row.id ? `latest-ai-set-track-${row.id}` : undefined}
                >
                  <span className="font-mono text-xs text-[#94A3B8] shrink-0 w-5 text-right">{row.i}</span>
                  <div className="min-w-0 flex-1">
                    <p className="font-normal text-[14px] sm:text-[15px] text-[#3B82F6] truncate">{row.t}</p>
                    <p className="md:hidden font-body text-[11px] text-[#94A3B8] truncate">{row.a}</p>
                  </div>
                </button>
                <div className="hidden md:block md:col-span-3 font-body text-sm text-[#64748B] truncate">{row.a}</div>
                <div className="col-span-2 md:col-span-1 font-mono text-sm text-[#475569] text-center">{row.bpm}</div>
                <div className={`col-span-2 md:col-span-1 font-mono text-sm font-bold text-center ${row.amber ? 'text-amber-500' : row.keyAccent ? 'text-[#2563FF]' : 'text-[#475569]'}`}>{row.key}</div>
                <div className="hidden md:flex md:col-span-2 items-center gap-2">
                  <div className="flex-1 h-1.5 rounded-full bg-[#F1F5F9] overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-[#2563FF] to-[#14E6C9]" style={{ width: `${Math.min(100, (row.e || 0) * 10)}%` }} />
                  </div>
                  <span className="font-mono text-xs text-[#64748B] w-7">{row.e}</span>
                  <button className="text-[#94A3B8] hover:text-[#2563FF]"><MoreHorizontal className="w-4 h-4" /></button>
                </div>
              </div>
            );
          })}
          </div>
        </div>

        {/* Mini player — surfaces the active row from the AI set above */}
        <div className="mt-5 flex items-center gap-4 rounded-xl bg-gradient-to-br from-white to-[#F8FAFC] border border-[#E2E8F0] p-3 shadow-[0_1px_3px_rgba(15,23,42,0.04)]">
          {(() => {
            const activeRowObj = displayRows.find((r) => r.i === activeRow) || displayRows[0];
            const activeTrack = activeRowObj?.track;
            // Is THIS row what's currently playing globally? If so, drive the
            // waveform from real audio.currentTime so it scrubs in lockstep.
            const isThisPlaying = isPlaying && playingTrack?.id && playingTrack.id === activeRowObj?.id;
            const duration = activeTrack?.duration || playingTrack?.duration || 1;
            return (
              <>
                <TrackCoverButton
                  track={activeTrack}
                  isCurrentlyPlaying={isThisPlaying}
                  isPlaying={isPlaying}
                  onPlayPause={() => activeRowObj && playSetTrack(activeRowObj)}
                  wrapperClassName="w-12 h-12"
                />
                <div className="min-w-0 hidden md:block w-40 shrink-0">
                  <p className="font-normal text-[15px] text-[#3B82F6] truncate" data-testid="dash-miniplayer-title">
                    {activeRowObj?.t || 'Pick a track'}
                  </p>
                  <p className="text-xs text-[#64748B] truncate">{activeRowObj?.a || ''}</p>
                </div>
                <DashboardMiniWaveform
                  track={activeTrack}
                  duration={duration}
                  isCurrentlyPlaying={isThisPlaying}
                />
                <div className="flex items-center gap-2 shrink-0">
                  <button className="w-8 h-8 rounded-full hover:bg-[#F1F5F9] text-[#64748B] flex items-center justify-center"><SkipBack className="w-4 h-4" /></button>
                  <button
                    onClick={() => activeRowObj && playSetTrack(activeRowObj)}
                    disabled={!activeRowObj?.id}
                    className="w-10 h-10 rounded-full gradient-primary flex items-center justify-center text-white shadow-[0_6px_16px_rgba(37,99,255,0.4)] disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="dash-miniplayer-play"
                  >
                    {isThisPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
                  </button>
                  <button className="w-8 h-8 rounded-full hover:bg-[#F1F5F9] text-[#64748B] flex items-center justify-center"><SkipForward className="w-4 h-4" /></button>
                  <button className="w-8 h-8 rounded-full hover:bg-[#F1F5F9] text-[#64748B] flex items-center justify-center"><ListMusic className="w-4 h-4" /></button>
                </div>
              </>
            );
          })()}
        </div>
    </section>
  );
}

/**
 * Dashboard mini-player waveform — wraps WaveformBars but polls the global
 * audio element's currentTime when this track is the live playing one so
 * the bars highlight in lockstep with playback. When not playing, it shows
 * the static waveform with no progress so users can still see the shape.
 */
function DashboardMiniWaveform({ track, duration, isCurrentlyPlaying }) {
  const [currentSec, setCurrentSec] = useState(0);
  useEffect(() => {
    if (!isCurrentlyPlaying) { setCurrentSec(0); return; }
    const tick = () => {
      const el = document.querySelector('audio[data-cratelab-music]');
      if (el && Number.isFinite(el.currentTime)) setCurrentSec(el.currentTime);
    };
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [isCurrentlyPlaying, track?.id]);
  const progress = Math.max(0, Math.min(100, (currentSec / Math.max(1, duration)) * 100));
  const fmt = (s) => {
    const m = Math.floor(s / 60); const r = Math.floor(s % 60);
    return `${m}:${r.toString().padStart(2, '0')}`;
  };
  return (
    <div className="flex-1 flex items-center gap-3 min-w-0">
      <span className="font-mono text-[10px] text-[#94A3B8] shrink-0 w-9 text-right">{fmt(currentSec)}</span>
      <div className="flex-1 h-9 rounded-md bg-[#F8FAFC] border border-[#E2E8F0] overflow-hidden relative">
        <WaveformBars
          structure={track?.analysis?.structure}
          duration={duration}
          progress={progress}
          trackId={track?.id}
        />
        {isCurrentlyPlaying && (
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-[#2563FF] pointer-events-none shadow-[0_0_8px_rgba(37,99,255,0.6)]"
            style={{ left: `${progress}%` }}
          />
        )}
      </div>
      <span className="font-mono text-[10px] text-[#94A3B8] shrink-0 w-9">{fmt(duration)}</span>
    </div>
  );
}

/* ───────────────────── helper components ───────────────────── */

function KpiCard({ icon: Icon, label, value, sub, desc }) {
  return (
    <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-sm p-6 flex items-center gap-6">
      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#EFF6FF] to-[#ECFEFF] border border-[#DBEAFE] flex items-center justify-center shrink-0">
        <Icon className="w-8 h-8 text-[#2563FF]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-mono text-[11px] uppercase tracking-wider text-[#94A3B8] mb-1">{label}</p>
        <p className="font-heading text-4xl font-bold text-[#0F172A] tabular-nums leading-none">{value}</p>
        <p className="font-body text-sm text-[#475569] mt-1.5">{sub}</p>
      </div>
      {desc && (
        <div className="hidden md:block max-w-xs border-l border-[#E2E8F0] pl-6">
          <p className="font-body text-sm text-[#64748B] leading-relaxed">{desc}</p>
        </div>
      )}
    </div>
  );
}

function ScoreKpi({ label, value, sub }) {
  const dash = (value / 100) * 138;
  return (
    <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-sm p-6 flex items-center gap-6">
      <div className="relative w-16 h-16 shrink-0">
        <svg viewBox="0 0 50 50" className="w-16 h-16 -rotate-90">
          <circle cx="25" cy="25" r="22" fill="none" stroke="#E2E8F0" strokeWidth="4" />
          <circle cx="25" cy="25" r="22" fill="none" stroke="url(#scoreGrad)" strokeWidth="4" strokeLinecap="round" strokeDasharray={`${dash} 138`} />
          <defs>
            <linearGradient id="scoreGrad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#2563FF" /><stop offset="100%" stopColor="#14E6C9" />
            </linearGradient>
          </defs>
        </svg>
        <span className="absolute inset-0 flex items-center justify-center font-mono text-[11px] font-bold text-[#2563FF]">{value}%</span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-mono text-[11px] uppercase tracking-wider text-[#94A3B8] mb-1">{label}</p>
        <p className="font-heading text-4xl font-bold text-[#0F172A] tabular-nums leading-none">{value}%</p>
        <p className="font-body text-sm text-[#475569] mt-1.5">{sub}</p>
      </div>
      <div className="hidden md:block max-w-xs border-l border-[#E2E8F0] pl-6">
        <p className="font-body text-sm text-[#64748B] leading-relaxed">How well your set tracks harmonically lock together on the Camelot wheel.</p>
      </div>
    </div>
  );
}

function MiniWaveform({ color = 'gradient' }) {
  return (
    <svg viewBox="0 0 400 60" preserveAspectRatio="none" className="w-full h-full">
      <defs>
        <linearGradient id="mwf" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#2563FF" /><stop offset="100%" stopColor="#14E6C9" />
        </linearGradient>
      </defs>
      {Array.from({ length: 80 }).map((_, i) => {
        const h = 12 + Math.abs(Math.sin(i * 0.35) * 22) + Math.abs(Math.cos(i * 0.18) * 8);
        return <rect key={i} x={i * 5} y={30 - h/2} width="2.5" height={h} rx="1" fill={color === 'primary' ? '#2563FF' : 'url(#mwf)'} opacity={0.6 + Math.sin(i*0.3) * 0.25} />;
      })}
    </svg>
  );
}

function EnergyFlowCard({ tracks }) {
  // Build a real SVG energy curve from the set's track-level energies.
  // Falls back to a smooth synthetic curve when there's no set yet.
  const W = 1000, H = 180;
  let pathLine = "M 0 145 C 60 135 100 115 180 90 S 320 60 480 55 S 680 70 800 75 S 920 80 1000 90";
  let pathFill = `${pathLine} L ${W} ${H} L 0 ${H} Z`;
  let labels = ['0:00', '15:00', '30:00', '45:00', '60:00'];
  let totalSec = 0;
  let minEnergy = 1, maxEnergy = 0, peakIdx = 0, dipIdx = 0;

  if (tracks && tracks.length > 1) {
    const pts = tracks.map((t, i) => {
      const raw = t.analysis?.energy ?? 0;
      const norm = raw > 1.5 ? raw / 100 : raw;
      const x = (i / (tracks.length - 1)) * W;
      const y = H - 15 - Math.max(0, Math.min(1, norm)) * (H - 30);
      totalSec += t.analysis?.duration || 0;
      if (norm > maxEnergy) { maxEnergy = norm; peakIdx = i; }
      if (norm < minEnergy) { minEnergy = norm; dipIdx = i; }
      return [x, y];
    });
    pathLine = pts.map(([x, y], i) => (i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`)).join(' ');
    pathFill = `${pathLine} L ${W} ${H} L 0 ${H} Z`;
    if (totalSec > 0) {
      const steps = 5;
      labels = Array.from({ length: steps }, (_, i) => {
        const sec = totalSec * (i / (steps - 1));
        const mm = Math.floor(sec / 60), ss = Math.floor(sec % 60).toString().padStart(2, '0');
        return `${mm}:${ss}`;
      });
    }
  }

  const handleWidgetClick = () => {
    const hasTracks = tracks && tracks.length > 1;
    const peakPos = hasTracks ? Math.round((peakIdx / (tracks.length - 1)) * 100) : 60;
    const dipPos = hasTracks ? Math.round((dipIdx / (tracks.length - 1)) * 100) : 30;
    const trend = hasTracks
      ? (tracks[tracks.length - 1]?.analysis?.energy ?? 0) > (tracks[0]?.analysis?.energy ?? 0)
        ? 'building upward'
        : 'winding down'
      : 'steadily building';
    const min = Math.round(minEnergy * 100), max = Math.round(maxEnergy * 100);
    window.dispatchEvent(new CustomEvent('cratelab:assistant-push', {
      detail: {
        content: `**Energy Flow Analysis**\n\nYour set is ${trend} overall. ${hasTracks ? `The energy peaks at track ${peakIdx + 1} (${max}%) and dips at track ${dipIdx + 1} (${min}%).` : 'Upload tracks and generate a set to see real energy data.'}\n\n**Tips:**\n• Peak energy around ${peakPos}% through your set is ideal for tech house — builds tension before the drop.\n• If energy dips too early (before 40%) your crowd may lose momentum — pad those low-energy tracks with a high-energy neighbour.\n• A gradual build for the first 30%, peak from 40-70%, then steady release feels most natural to dancers.`,
        title: 'Energy Flow',
        openRail: true,
      }
    }));
  };

  return (
    <div
      className="bg-white rounded-2xl border border-[#E2E8F0] shadow-sm p-6 cursor-pointer hover:border-[#2563FF]/40 hover:shadow-md transition-all"
      data-testid="dash-energy-flow"
      onClick={handleWidgetClick}
      title="Click for AI analysis"
    >
      <div className="flex items-start justify-between mb-4 gap-4">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-wider text-[#2563FF] font-semibold mb-1">Energy Flow</p>
          <p className="text-sm text-[#64748B] leading-snug max-w-xl">How your set builds and releases intensity across every track — spot dips and peaks before you play.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-[10px] font-mono uppercase tracking-wider text-[#94A3B8] mt-1">
            {tracks && tracks.length > 1 ? `${tracks.length} tracks` : 'sample'}
          </span>
          <span className="text-[10px] font-mono uppercase tracking-wider text-[#2563FF] border border-[#DBEAFE] bg-[#EFF6FF] rounded px-1.5 py-0.5 mt-1">AI tip ↗</span>
        </div>
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className="w-full h-44"
      >
        <defs>
          <linearGradient id="efl" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#2563FF" stopOpacity={0.3} />
            <stop offset="100%" stopColor="#14E6C9" stopOpacity={0} />
          </linearGradient>
        </defs>
        <path d={pathFill} fill="url(#efl)" />
        <path d={pathLine} fill="none" stroke="#2563FF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <div className="flex justify-between font-mono text-[10px] text-[#94A3B8] mt-2">
        {labels.map((l, i) => <span key={i}>{l}</span>)}
      </div>
    </div>
  );
}

function HarmonicMapCard({ tracks }) {
  const [selectedKey, setSelectedKey] = useState(null);

  // Build {code: count} from real tracks for the CamelotWheel trackCounts prop
  const keyCounts = {};
  let bpmTotal = 0, bpmN = 0;
  if (tracks) {
    tracks.forEach((t) => {
      const k = t.analysis?.camelot;
      if (k) keyCounts[k] = (keyCounts[k] || 0) + 1;
      if (t.analysis?.bpm) { bpmTotal += t.analysis.bpm; bpmN++; }
    });
  }
  const avgBpm = bpmN ? Math.round(bpmTotal / bpmN) : '—';
  const topKeys = Object.entries(keyCounts).sort((a, b) => b[1] - a[1]).slice(0, 4);

  const handleKeySelect = (code) => {
    setSelectedKey(code);
    if (!code) return;
    const seg = { '1A':'A♭m','2A':'E♭m','3A':'B♭m','4A':'Fm','5A':'Cm','6A':'Gm','7A':'Dm','8A':'Am','9A':'Em','10A':'Bm','11A':'F#m','12A':'C#m','1B':'B','2B':'G♭','3B':'D♭','4B':'A♭','5B':'E♭','6B':'B♭','7B':'F','8B':'C','9B':'G','10B':'D','11B':'A','12B':'E' };
    const keyName = seg[code] || code;
    const num = parseInt(code.slice(0, -1));
    const letter = code.slice(-1);
    const compat = [
      code,
      `${num === 12 ? 1 : num + 1}${letter}`,
      `${num === 1 ? 12 : num - 1}${letter}`,
      `${num}${letter === 'A' ? 'B' : 'A'}`
    ].filter(Boolean);
    const count = keyCounts[code] || 0;
    window.dispatchEvent(new CustomEvent('cratelab:assistant-push', {
      detail: {
        content: `**${code} — ${keyName}**\n\nYou have **${count} track${count !== 1 ? 's' : ''}** in ${code} in this set.\n\n**Compatible keys to mix into:** ${compat.join(', ')}\n\nThese keys sit adjacent on the Camelot wheel — transitions between them will sound harmonically smooth with no key clash.\n\n**Tip:** Mix within the same number (e.g. 8A → 8B) for a mood shift from minor to major. Move ±1 number (e.g. 8A → 9A) for a gradual energy build. Jump 2+ positions for a tension/surprise effect — use sparingly.`,
        title: `Key: ${code}`,
        openRail: true,
      }
    }));
  };

  const handleCardClick = (e) => {
    // Only fire the general overview if clicking the card background (not a wheel segment)
    if (e.target.closest('path') || e.target.closest('text')) return;
    const keyList = topKeys.map(([k, c]) => `${k}×${c}`).join(', ') || 'no key data yet';
    window.dispatchEvent(new CustomEvent('cratelab:assistant-push', {
      detail: {
        content: `**Harmonic Map Overview**\n\nAverage BPM across your set: **${avgBpm}**.\n\nMost-used keys in this set: **${keyList}**.\n\nClick any segment on the Camelot wheel to see compatible keys and get mixing tips for that specific key.\n\n**Reading the wheel:** The outer ring is major keys (B), the inner ring is minor (A). Segments that share a number (e.g. 8A and 8B) are relative major/minor — they mix together perfectly.`,
        title: 'Harmonic Map',
        openRail: true,
      }
    }));
  };

  return (
    <div
      className="bg-white rounded-2xl border border-[#E2E8F0] shadow-sm p-6 cursor-pointer hover:border-[#2563FF]/40 hover:shadow-md transition-all"
      data-testid="dash-harmonic-map"
      onClick={handleCardClick}
    >
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Wheel */}
        <div className="flex items-center justify-center shrink-0">
          <CamelotWheel
            selectedKey={selectedKey}
            onSelectKey={handleKeySelect}
            trackCounts={keyCounts}
            size={320}
          />
        </div>
        {/* Info panel */}
        <div className="flex-1 min-w-0 flex flex-col justify-center">
          <div className="mb-4">
            <p className="font-mono text-[11px] uppercase tracking-wider text-[#2563FF] font-semibold mb-1">Harmonic Map</p>
            <p className="text-sm text-[#64748B] leading-snug">Your set's key distribution on the Camelot wheel. Click any segment to see compatible transitions and get an AI tip.</p>
          </div>
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-[#F8FAFC] rounded-xl border border-[#E2E8F0] px-5 py-3">
              <p className="font-mono text-[10px] text-[#94A3B8] uppercase tracking-wider mb-1">Avg BPM</p>
              <p className="font-heading text-3xl font-bold text-[#0F172A]">{avgBpm}</p>
            </div>
            <div className="bg-gradient-to-br from-[#EFF6FF] to-[#ECFEFF] rounded-xl border border-[#DBEAFE] px-5 py-3">
              <p className="font-mono text-[10px] text-[#2563FF] uppercase tracking-wider mb-1">Keys Used</p>
              <p className="font-heading text-3xl font-bold text-[#2563FF]">{Object.keys(keyCounts).length || '—'}</p>
            </div>
          </div>
          {topKeys.length > 0 && (
            <div>
              <p className="font-mono text-[10px] uppercase tracking-wider text-[#94A3B8] mb-2">Top Keys</p>
              <div className="flex flex-wrap gap-2">
                {topKeys.map(([k, count]) => (
                  <button
                    key={k}
                    onClick={(e) => { e.stopPropagation(); handleKeySelect(k === selectedKey ? null : k); }}
                    className={`px-3 py-1.5 rounded-lg font-mono text-sm font-semibold transition-all ${
                      selectedKey === k
                        ? 'bg-[#2563FF] text-white border border-[#2563FF]'
                        : 'bg-[#EFF6FF] border border-[#DBEAFE] text-[#2563FF] hover:bg-[#DBEAFE]'
                    }`}
                  >
                    {k} <span className="opacity-60 font-normal text-xs">×{count}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {!topKeys.length && (
            <p className="text-sm text-[#94A3B8] italic mt-2">Generate an AI set to see key distribution</p>
          )}
          <p className="mt-4 text-[10px] font-mono uppercase tracking-wider text-[#2563FF] opacity-60">Click wheel or key → AI tip ↗</p>
        </div>
      </div>
    </div>
  );
}

function SetInsightsCard({ onView, tracks }) {
  // Derive 3 insights from real set data; fall back to placeholder copy.
  const items = useMemo(() => {
    if (!tracks || tracks.length < 2) {
      return [
        { tone: 'good', txt: 'Great energy progression with smooth build up.' },
        { tone: 'good', txt: 'Good variation in keys and track selection.' },
        { tone: 'warn', txt: 'Consider adding a reset track around 60 min.' },
      ];
    }
    const out = [];
    // BPM smoothness
    let maxJump = 0;
    for (let i = 1; i < tracks.length; i++) {
      const a = tracks[i - 1].analysis?.bpm || 0;
      const b = tracks[i].analysis?.bpm || 0;
      if (a > 0 && b > 0) maxJump = Math.max(maxJump, Math.abs(b - a) / a * 100);
    }
    out.push(maxJump <= 5
      ? { tone: 'good', txt: `Smooth BPM flow — biggest step is just ${maxJump.toFixed(1)}%.` }
      : { tone: 'warn', txt: `BPM jump of ${maxJump.toFixed(1)}% detected — listen ahead for crowd loss.` });

    // Energy progression
    const energies = tracks.map(t => {
      const r = t.analysis?.energy ?? 0;
      return r > 1.5 ? r / 100 : r;
    });
    const monotone = energies.every((e, i) => i === 0 || e >= energies[i - 1] - 0.05);
    out.push(monotone
      ? { tone: 'good', txt: 'Energy curve climbs cleanly with no big dips.' }
      : { tone: 'warn', txt: 'Energy dips mid-set — consider re-ordering to keep the floor lifted.' });

    // Key variety
    const uniqueKeys = new Set(tracks.map(t => t.analysis?.camelot).filter(Boolean)).size;
    out.push(uniqueKeys >= Math.max(3, Math.floor(tracks.length / 3))
      ? { tone: 'good', txt: `${uniqueKeys} unique Camelot keys — healthy harmonic variety.` }
      : { tone: 'warn', txt: `Only ${uniqueKeys} unique key${uniqueKeys === 1 ? '' : 's'} — set may feel monotonous.` });
    return out;
  }, [tracks]);

  const handleCardClick = () => {
    const warnings = items.filter(it => it.tone === 'warn');
    const goods = items.filter(it => it.tone === 'good');
    const hasTracks = tracks && tracks.length >= 2;

    const scoreLabel = warnings.length === 0 ? '🟢 Excellent'
      : warnings.length === 1 ? '🟡 Good — minor issues'
      : '🔴 Needs attention';

    const warningLines = warnings.map(w => `• ⚠ ${w.txt}`).join('\n');
    const goodLines = goods.map(g => `• ✓ ${g.txt}`).join('\n');

    const tip = !hasTracks
      ? "Generate a set first to unlock personalised AI scoring."
      : warnings.some(w => w.txt.includes('BPM'))
        ? "**Re-ordering tip:** Sort your set by BPM ascending — this creates a natural momentum arc. Avoid jumping more than 4-5% BPM between back-to-back tracks."
        : warnings.some(w => w.txt.includes('Energy') || w.txt.includes('energy'))
          ? "**Re-ordering tip:** Place your highest-energy tracks between 40-70% through your set. If you have a low-energy track mid-set, buffer it with a medium-energy track before and after."
          : warnings.some(w => w.txt.includes('key') || w.txt.includes('Key'))
            ? "**Re-ordering tip:** Introduce a new Camelot key every 3-4 tracks to add harmonic movement. Try moving ±1 number (e.g. 8A → 9A) for a smooth energy lift."
            : "Your set is scoring well — no significant changes needed. Keep this balance going!";

    window.dispatchEvent(new CustomEvent('cratelab:assistant-push', {
      detail: {
        content: `**Set Insights — ${scoreLabel}**\n\n${goodLines}${warningLines ? '\n' + warningLines : ''}\n\n${tip}\n\n*Click "View Full Analysis →" on the widget for the complete set breakdown.*`,
        title: 'Set Insights',
        openRail: true,
      }
    }));
  };

  return (
    <div
      className="bg-white rounded-2xl border border-[#E2E8F0] shadow-sm p-6 cursor-pointer hover:border-[#2563FF]/40 hover:shadow-md transition-all"
      data-testid="dash-set-insights"
      onClick={handleCardClick}
    >
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-wider text-[#2563FF] font-semibold mb-1">Set Insights</p>
          <p className="text-sm text-[#64748B] leading-snug">Real-time health check — BPM smoothness, energy arc, and key variety scored automatically from your latest set.</p>
        </div>
        <span className="text-[10px] font-mono uppercase tracking-wider text-[#2563FF] border border-[#DBEAFE] bg-[#EFF6FF] rounded px-1.5 py-0.5 shrink-0 mt-1">AI tip ↗</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {items.map((it, i) => (
          <div key={i} className={`flex gap-3 p-4 rounded-xl border ${it.tone === 'good' ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
            <span className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0 text-[11px] font-bold ${it.tone === 'good' ? 'bg-emerald-100 text-emerald-600 border border-emerald-300' : 'bg-amber-100 text-amber-600 border border-amber-300'}`}>
              {it.tone === 'good' ? '✓' : '!'}
            </span>
            <p className={`font-body text-sm leading-snug ${it.tone === 'good' ? 'text-emerald-800' : 'text-amber-800'}`}>{it.txt}</p>
          </div>
        ))}
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onView(); }}
        className="mt-4 w-full py-3 rounded-xl bg-[#EFF6FF] border border-[#DBEAFE] text-[#2563FF] text-sm font-semibold hover:bg-[#DBEAFE] transition-colors"
      >
        View Full Analysis →
      </button>
    </div>
  );
}

/* ─────────────── Transition card (dynamic, context-aware) ───────────────
 * Picks the "from" track from (in priority order):
 *   1. The currently playing track (PlayerContext.playingTrack)
 *   2. The first track of the latest AI-generated set
 * Picks the "to" track from:
 *   1. The next track in the latest set if "from" lives in that set
 *   2. The most Camelot-compatible track in the latest set otherwise
 * Falls back to a friendly empty state when the user has no set yet.
 */
const CURVE_LABEL = {
  build: 'build',
  warmup: 'warmup',
  hard_peak_then_cooldown: 'peak → cooldown',
  peaks_and_valleys: 'peaks & valleys',
};

function camelotCompatible(code) {
  if (!code || typeof code !== 'string') return [];
  const n = parseInt(code.slice(0, -1), 10);
  const l = code.slice(-1);
  if (!n || (l !== 'A' && l !== 'B')) return [];
  const next = `${n === 12 ? 1 : n + 1}${l}`;
  const prev = `${n === 1 ? 12 : n - 1}${l}`;
  const rel = `${n}${l === 'A' ? 'B' : 'A'}`;
  return [code, next, prev, rel];
}

function compatibilityScore(fromKey, toKey) {
  if (!fromKey || !toKey) return 70;
  if (fromKey === toKey) return 100;
  const compat = camelotCompatible(fromKey);
  if (compat.includes(toKey)) return 92;
  // 2 steps away on the wheel
  const fn = parseInt(fromKey.slice(0, -1), 10);
  const tn = parseInt(toKey.slice(0, -1), 10);
  const fl = fromKey.slice(-1);
  const tl = toKey.slice(-1);
  if (fl === tl) {
    const diff = Math.min(Math.abs(fn - tn), 12 - Math.abs(fn - tn));
    if (diff === 2) return 75;
  }
  return 55;
}

function pickTransition(from, pool) {
  if (!from || !pool || !pool.length) return null;
  // If from is in pool, prefer the next slot in sequence
  const idx = pool.findIndex((t) => t.id === from.id);
  if (idx >= 0 && idx < pool.length - 1) {
    return pool[idx + 1];
  }
  // Otherwise rank remaining tracks by Camelot compatibility, then BPM
  const fromKey = from?.analysis?.camelot;
  const fromBpm = from?.analysis?.bpm || 0;
  const ranked = pool
    .filter((t) => t.id !== from.id && t?.analysis?.camelot)
    .map((t) => ({
      t,
      score: compatibilityScore(fromKey, t.analysis.camelot),
      bpmDiff: Math.abs((t.analysis?.bpm || 0) - fromBpm),
    }))
    .sort((a, b) => (b.score - a.score) || (a.bpmDiff - b.bpmDiff));
  return ranked[0]?.t || null;
}

function TransitionCard({ playingTrack, setTracks, navigate }) {
  const fromTrack = playingTrack || (setTracks && setTracks[0]) || null;
  const toTrack = useMemo(() => {
    if (!fromTrack) return null;
    return pickTransition(fromTrack, setTracks || []);
  }, [fromTrack, setTracks]);

  // Empty state — no AI set yet and nothing is playing
  if (!fromTrack || !toTrack) {
    return (
      <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-sm p-6" data-testid="dash-transition-empty">
        <div className="mb-4">
          <p className="font-mono text-[11px] uppercase tracking-wider text-[#2563FF] font-semibold mb-1">Next Transition</p>
          <p className="text-sm text-[#64748B] leading-snug">Your best harmonic next-track suggestion — based on key, BPM, and energy compatibility.</p>
        </div>
        <div className="flex items-center gap-6 py-4">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#EFF6FF] to-[#ECFEFF] border border-[#DBEAFE] flex items-center justify-center shrink-0">
            <Sparkles className="w-7 h-7 text-[#2563FF]" />
          </div>
          <div>
            <p className="font-body text-sm text-[#475569] leading-snug mb-3 max-w-lg">
              Generate a set or hit play on a track and CRATELAB will suggest the next harmonic transition.
            </p>
            <Link
              to="/sets"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#EFF6FF] border border-[#DBEAFE] text-[11px] font-mono uppercase tracking-wider text-[#2563FF] hover:bg-[#DBEAFE] transition-colors"
              data-testid="dash-transition-cta"
            >
              BUILD A SET →
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const fromKey = fromTrack.analysis?.camelot || '—';
  const fromBpm = fromTrack.analysis?.bpm ? Math.round(fromTrack.analysis.bpm) : '—';
  const toKey = toTrack.analysis?.camelot || '—';
  const toBpm = toTrack.analysis?.bpm ? Math.round(toTrack.analysis.bpm) : '—';
  const score = compatibilityScore(fromKey, toKey);
  const bpmDelta = (fromTrack.analysis?.bpm && toTrack.analysis?.bpm)
    ? Math.round(toTrack.analysis.bpm - fromTrack.analysis.bpm) : 0;
  const harmonic = score >= 92 ? 'Excellent' : score >= 75 ? 'Good' : 'Risky';
  const energyDelta = (() => {
    const r = (t) => {
      const e = t.analysis?.energy ?? 0;
      return e > 1.5 ? e / 100 : e;
    };
    return r(toTrack) - r(fromTrack);
  })();
  const energyLabel = Math.abs(energyDelta) < 0.08
    ? 'Steady'
    : energyDelta > 0 ? 'Lifts' : 'Drops';

  return (
    <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-sm p-6" data-testid="dash-transition">
      <div className="mb-4">
        <p className="font-mono text-[11px] uppercase tracking-wider text-[#2563FF] font-semibold mb-1">
          {playingTrack ? 'Suggested Next' : 'Next Transition'}
        </p>
        <p className="text-sm text-[#64748B] leading-snug">CRATELAB picks the most harmonic next track based on Camelot key, BPM delta, and energy curve.</p>
      </div>
      <div className="flex flex-col md:flex-row gap-6">
        {/* From → To section */}
        <div className="flex-1 min-w-0">
          <button
            onClick={() => fromTrack.id && navigate(`/track/${fromTrack.id}`)}
            className="w-full flex items-center gap-3 mb-3 text-left hover:bg-[#F8FAFC] rounded-xl p-3 transition-colors"
            data-testid="dash-transition-from"
          >
            <div className="w-14 h-14 rounded-xl overflow-hidden border border-[#E2E8F0] bg-gradient-to-br from-[#EFF6FF] to-[#ECFEFF] flex items-center justify-center flex-shrink-0">
              {fromTrack.cover_url ? (
                <SafeImg src={fromTrack.cover_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <Music className="w-5 h-5 text-[#2563FF]" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-mono text-[9px] uppercase tracking-wider text-[#94A3B8] mb-0.5">Now Playing</p>
              <p className="font-semibold text-base text-[#0F172A] line-clamp-2 leading-snug">{fromTrack.title || 'Untitled'}</p>
              <p className="text-sm text-[#64748B] mt-0.5">{fromTrack.artist || 'Unknown'} · <span className="font-mono">{fromBpm} BPM {fromKey}</span></p>
            </div>
          </button>
          <div className="h-7 my-1 px-3"><MiniWaveform color="primary" /></div>
          <button
            onClick={() => toTrack.id && navigate(`/track/${toTrack.id}`)}
            className="w-full flex items-center gap-3 mt-3 text-left hover:bg-[#F8FAFC] rounded-xl p-3 transition-colors border border-[#DBEAFE] bg-[#F8FBFF]"
            data-testid="dash-transition-to"
          >
            <div className="w-14 h-14 rounded-xl overflow-hidden border border-[#DBEAFE] bg-gradient-to-br from-[#EFF6FF] to-[#ECFEFF] flex items-center justify-center flex-shrink-0">
              {toTrack.cover_url ? (
                <SafeImg src={toTrack.cover_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <Music className="w-5 h-5 text-[#2563FF]" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-mono text-[9px] uppercase tracking-wider text-[#2563FF] mb-0.5">Suggested Next</p>
              <p className="font-semibold text-base text-[#0F172A] line-clamp-2 leading-snug">{toTrack.title || 'Untitled'}</p>
              <p className="text-sm text-[#64748B] mt-0.5">{toTrack.artist || 'Unknown'} · <span className="font-mono">{toBpm} BPM {toKey}</span></p>
            </div>
          </button>
        </div>
        {/* Compatibility stats */}
        <div className="md:w-64 shrink-0 flex flex-col justify-center">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="font-mono text-[#94A3B8] uppercase tracking-wider text-[10px]">Compatibility</span>
            <span className="font-heading text-2xl font-bold text-[#2563FF]" data-testid="dash-transition-score">{score}%</span>
          </div>
          <div className="h-2 rounded-full bg-[#F1F5F9] overflow-hidden mb-4">
            <div className="h-full rounded-full bg-gradient-to-r from-[#2563FF] to-[#14E6C9]" style={{ width: `${score}%` }} />
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <Pill label="Harmonic" value={harmonic} tone={score >= 92 ? 'primary' : undefined} />
            <Pill label="BPM Δ" value={`${bpmDelta >= 0 ? '+' : ''}${bpmDelta}`} />
            <Pill label="Energy" value={energyLabel} tone={energyLabel !== 'Drops' ? 'primary' : undefined} />
          </div>
        </div>
      </div>
    </div>
  );
}

function Pill({ label, value, tone }) {
  return (
    <div>
      <p className="font-mono text-[9px] uppercase tracking-wide text-[#94A3B8] mb-1">{label}</p>
      <span className={`block px-2 py-1 rounded-md text-[11px] font-mono font-medium ${tone === 'primary' ? 'bg-[#EFF6FF] text-[#2563FF] border border-[#DBEAFE]' : 'bg-[#F1F5F9] text-[#475569] border border-[#E2E8F0]'}`}>{value}</span>
    </div>
  );
}


/* Stack of up to 4 overlapping cover thumbnails — used as the album-art
   hero in the Latest AI Set widget header. Tracks without artwork fall
   back to a gradient + Music icon tile so the stack always looks full.
   When a playlist id is supplied the whole stack becomes a clickable
   shortcut to the full set page. */
function CoverStack({ tracks = [], playlistId }) {
  const picks = tracks.slice(0, 4);
  if (picks.length === 0) return null;
  const inner = (
    <div className="flex -space-x-3 shrink-0">
      {picks.map((t, i) => (
        <div
          key={t?.id || i}
          className="w-12 h-12 rounded-xl overflow-hidden border-2 border-white shadow-[0_2px_6px_rgba(15,23,42,0.10)] bg-gradient-to-br from-[#EFF6FF] to-[#ECFEFF] flex items-center justify-center"
          style={{ zIndex: 10 - i }}
        >
          {t?.cover_url ? (
            <SafeImg src={t.cover_url} alt="" className="w-full h-full object-cover" />
          ) : (
            <Music className="w-4 h-4 text-[#2563FF]" />
          )}
        </div>
      ))}
    </div>
  );
  if (playlistId) {
    return (
      <Link
        to={`/playlist/${playlistId}`}
        className="shrink-0 transition-transform hover:scale-[1.03] focus:outline-none focus:ring-2 focus:ring-[#2563FF]/30 rounded-xl"
        data-testid="latest-ai-set-cover-stack"
        title="Open full set"
      >
        {inner}
      </Link>
    );
  }
  return (
    <div className="shrink-0" data-testid="latest-ai-set-cover-stack" aria-hidden="true">
      {inner}
    </div>
  );
}
